import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDrag } from '@use-gesture/react';
import { Book } from '@/utils/bookRouting';
import { formatTime, getDisplayedPlaybackTime } from '@/utils/timeUtils';
import AlephEntry, { type ConversationMode } from './AlephEntry';
import PlayerControls from './PlayerControls';
import { useTheme } from '@/contexts/ThemeContext';
import ProgressBar from './ProgressBar';
import TranscribePlayback from './TranscribePlayback';
import ChapterSelectionIcon from "../components/icons/ChapterSelectionIcon";
import ClockIcon from './icons/ClockIcon';
import MoreOptionsIcon from './icons/MoreOptionsIcon';
import ChapterList from './ChapterList';
import useClickOutside from '../hooks/useClickOutside';
import { PipecatClientProvider } from '@pipecat-ai/client-react';
import { PipecatClient } from '@pipecat-ai/client-js';
import ConversationVisualizer from "./ConversationVisualizer";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { bookAnalytics } from '@/services/bookAnalytics';
import { motion } from "framer-motion";
import { ReaderBook, ReaderChapter } from '@/services/booksApi';
import { observeStableVisualViewport } from '@/utils/stableVisualViewport';
import {
  READER_SIDE_PANEL_SPRING_TRANSITION,
  READER_SIDE_PANEL_WIDTH_PX,
} from '@/utils/textChatLayout';
import SleepTimerMenu, { getSleepTimerButtonLabel } from './SleepTimerMenu';
import PlaybackSpeedControl from './PlaybackSpeedControl';
import { SleepTimerState } from '@/hooks/useSleepTimer';
import type {
  AnalyticsInputMethod,
  InteractionContext,
  VoiceEndContext,
  VoiceStartContext,
} from '@/services/analyticsContext';
import { ChapterTimingsStatus } from '@/hooks/useChapterAudio';
import ControlPopupPresence from './ControlPopupPresence';
import PlayerMoreSheet, { PlayerMoreScrim } from './PlayerMoreSheet';
import { isNativeApp } from '@/utils/platform';
import { ChapterRuntime } from '@/utils/ordinalSync';
import type { ChatSessionSummary } from '@/types/chat';
import type {
  ReaderContentReadiness,
  ReaderChapterSelectionRequest,
  ReaderPositionAcknowledgement,
  ReaderPositionRequest,
} from '@/types/readerReadiness';

type ButtonSize = "base" | "sm" | "md" | "lg" | "xl";

interface ReadPlayerProps {
  book: Book;
  isPlaying: boolean;
  isReady: boolean;
  currentTime: number;
  duration: number;
  onTogglePlay: () => void;
  onSkipForward: () => void;
  onSkipBackward: () => void;
  getProgressSeconds: () => number;
  onConnect: (context: VoiceStartContext) => Promise<boolean>;
  onDisconnect: (context: VoiceEndContext) => Promise<void>;
  isConnected: boolean;
  connectionStatus: string;
  isBuffering: boolean;
  onSeek: (time: number) => void;
  currentChapter: ReaderChapter | null;
  chapters: ReaderChapter[];
  onChapterSelect: (chapterId: string, options?: { fragmentId?: string | null }) => void;
  chapterSelectionRequest: ReaderChapterSelectionRequest | null;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  rtviClientRef: React.RefObject<PipecatClient | null>;
  currentSpeaker: 'local' | 'bot';
  readerBook: ReaderBook | null;
  isRenderProfileSupported: boolean;
  chapterRuntime: ChapterRuntime | null;
  chapterTimingsStatus: ChapterTimingsStatus;
  selectedReadingVoiceKey: string;
  isLoadingReaderBook: boolean;
  readerBookError: string | null;
  onRetryReaderBook: () => void;
  chapterRecoveryNotice: string | null;
  positionRequest: ReaderPositionRequest | null;
  pendingResumeSeconds: number | null;
  onReaderContentReadinessChange: (readiness: ReaderContentReadiness | null) => void;
  onReaderPositioned: (acknowledgement: ReaderPositionAcknowledgement | null) => void;
  onSeekAndPlay: (time: number, targetChapterId?: string) => void;
  onOpenTextChat?: (
    initialMessage?: string,
    forceOpen?: boolean,
    context?: InteractionContext,
  ) => void;
  isTextChatOpen?: boolean;
  textChatDraft?: string;
  /** The book's anchored conversations, for the reader's margin dots. */
  conversationSessions?: readonly ChatSessionSummary[];
  activeConversationId?: number | null;
  onOpenConversation?: (session: ChatSessionSummary, inputMethod: AnalyticsInputMethod) => void;
  sleepTimer: SleepTimerState | null;
  sleepTimerRemainingSeconds: number | null;
  onSetCountdownSleepTimer: (minutes: number) => void;
  onSetEndOfChapterSleepTimer: () => void;
  onCancelSleepTimer: () => void;
  playbackRate: number;
  onPlaybackRateChange: (rate: number) => void;
  conversationMode: ConversationMode;
  onConversationModeChange: (mode: ConversationMode) => void;
}

interface ReaderTimeInfoProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  currentTime: number;
  duration: number;
  imperativePlayback: boolean;
  pendingResumeSeconds: number | null;
}

const ReaderTimeInfo: React.FC<ReaderTimeInfoProps> = ({
  audioRef,
  currentTime,
  duration,
  imperativePlayback,
  pendingResumeSeconds,
}) => {
  const currentTimeRef = useRef<HTMLSpanElement>(null);
  const chapterTimeLeftRef = useRef<HTMLSpanElement>(null);
  const renderedLabelsRef = useRef<{
    currentTime: string | null;
    chapterTimeLeft: string | null;
  }>({ currentTime: null, chapterTimeLeft: null });

  const updateLabels = useCallback(() => {
    const audioTime = getDisplayedPlaybackTime(audioRef.current, currentTime, pendingResumeSeconds);
    const nextCurrentTime = formatTime(audioTime);
    if (currentTimeRef.current && renderedLabelsRef.current.currentTime !== nextCurrentTime) {
      currentTimeRef.current.textContent = nextCurrentTime;
      renderedLabelsRef.current.currentTime = nextCurrentTime;
    }

    const nextChapterTimeLeft = `-${formatTime(Math.max(0, duration - audioTime))}`;
    if (chapterTimeLeftRef.current && renderedLabelsRef.current.chapterTimeLeft !== nextChapterTimeLeft) {
      chapterTimeLeftRef.current.textContent = nextChapterTimeLeft;
      renderedLabelsRef.current.chapterTimeLeft = nextChapterTimeLeft;
    }
  }, [audioRef, currentTime, duration, pendingResumeSeconds]);

  useEffect(() => {
    if (!imperativePlayback) {
      return;
    }

    const audio = audioRef.current;
    let intervalId: number | null = null;

    renderedLabelsRef.current = {
      currentTime: currentTimeRef.current?.textContent ?? null,
      chapterTimeLeft: chapterTimeLeftRef.current?.textContent ?? null,
    };

    const stopInterval = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    const startInterval = () => {
      updateLabels();
      if (!audio || audio.paused || audio.ended) {
        stopInterval();
        return;
      }
      if (intervalId === null) {
        intervalId = window.setInterval(updateLabels, 250);
      }
    };

    updateLabels();
    startInterval();

    audio?.addEventListener('play', startInterval);
    audio?.addEventListener('playing', startInterval);
    audio?.addEventListener('pause', stopInterval);
    audio?.addEventListener('ended', stopInterval);
    audio?.addEventListener('seeked', updateLabels);
    audio?.addEventListener('timeupdate', updateLabels);
    audio?.addEventListener('loadedmetadata', updateLabels);

    return () => {
      stopInterval();
      audio?.removeEventListener('play', startInterval);
      audio?.removeEventListener('playing', startInterval);
      audio?.removeEventListener('pause', stopInterval);
      audio?.removeEventListener('ended', stopInterval);
      audio?.removeEventListener('seeked', updateLabels);
      audio?.removeEventListener('timeupdate', updateLabels);
      audio?.removeEventListener('loadedmetadata', updateLabels);
    };
  }, [audioRef, imperativePlayback, updateLabels]);

  if (!imperativePlayback) {
    const displayedTime = pendingResumeSeconds ?? currentTime;
    return (
      <div className="contents text-sm select-none">
        <span className="order-first shrink-0">{formatTime(displayedTime)}</span>
        <span className="order-last shrink-0">
          -{formatTime(Math.max(0, duration - displayedTime))}
        </span>
      </div>
    );
  }

  const displayedTime = getDisplayedPlaybackTime(audioRef.current, currentTime, pendingResumeSeconds);
  return (
    <div className="contents text-sm select-none">
      <span ref={currentTimeRef} className="order-first shrink-0">{formatTime(displayedTime)}</span>
      <span ref={chapterTimeLeftRef} className="order-last shrink-0">
        -{formatTime(Math.max(0, duration - displayedTime))}
      </span>
    </div>
  );
};

const ReadPlayerComponent: React.FC<ReadPlayerProps> = ({
                                                             book,
                                                             isPlaying,
                                                             isReady,
                                                             currentTime,
                                                             duration,
                                                             onTogglePlay,
                                                             onSkipForward,
                                                             onSkipBackward,
                                                             getProgressSeconds,
                                                             onConnect,
                                                             onDisconnect,
                                                             isConnected,
                                                             connectionStatus,
                                                             isBuffering,
                                                             onSeek,
                                                             currentChapter,
                                                             chapters,
                                                             onChapterSelect,
                                                             chapterSelectionRequest,
                                                             audioRef,
                                                             rtviClientRef,
                                                             currentSpeaker,
                                                             readerBook,
                                                             isRenderProfileSupported,
                                                             chapterRuntime,
                                                             chapterTimingsStatus,
                                                             selectedReadingVoiceKey,
                                                             isLoadingReaderBook,
                                                             readerBookError,
                                                             onRetryReaderBook,
                                                             chapterRecoveryNotice,
                                                             positionRequest,
                                                             pendingResumeSeconds,
                                                             onReaderContentReadinessChange,
                                                             onReaderPositioned,
                                                             onSeekAndPlay,
                                                             onOpenTextChat,
                                                             isTextChatOpen = false,
                                                             textChatDraft,
                                                             conversationSessions,
                                                             activeConversationId,
                                                             onOpenConversation,
                                                             sleepTimer,
                                                             sleepTimerRemainingSeconds,
                                                             onSetCountdownSleepTimer,
                                                             onSetEndOfChapterSleepTimer,
                                                             onCancelSleepTimer,
                                                             playbackRate,
                                                             onPlaybackRateChange,
                                                             conversationMode,
                                                             onConversationModeChange,
                                                           }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const themeClasses = isDarkMode ? 'text-theme-primary-dark' : 'bg-theme-light var(--color-gray-800)';
  const [showChapterList, setShowChapterList] = useState(false);
  const [showSleepTimer, setShowSleepTimer] = useState(false);
  const [showPlaybackSpeed, setShowPlaybackSpeed] = useState(false);
  // The "more" sheet. In the native app the leading transport button opens
  // it, and the sleep timer lives inside beside the book download; on the
  // web there is nothing to put beside the timer, so it keeps its own
  // one-tap button and popover.
  const usesMoreSheet = isNativeApp();
  const [showMore, setShowMore] = useState(false);
  // True from opening until the closing animation ends: while the scrim is
  // on screen the controls need a background of their own to sit above it.
  const [moreSurfaceMounted, setMoreSurfaceMounted] = useState(false);
  const viewportContentRef = useRef<HTMLDivElement>(null);
  const chapterSelectorRef = useRef<HTMLDivElement>(null);
  const sleepTimerRef = useRef<HTMLDivElement>(null);
  const breakpoint = useBreakpoint();
  const isCurrentReaderBook = readerBook?.id === book.id;
  const shouldMountTranscript = isCurrentReaderBook;

  useEffect(() => {
    if (isLoadingReaderBook || readerBookError || !isCurrentReaderBook) {
      onReaderContentReadinessChange(null);
      onReaderPositioned(null);
    }
  }, [
    isCurrentReaderBook,
    isLoadingReaderBook,
    onReaderContentReadinessChange,
    onReaderPositioned,
    readerBookError,
  ]);

  const usesMobileChatLayout = breakpoint !== '2xl';
  const isMobileOrTablet = breakpoint !== 'lg' && breakpoint !== 'xl' && breakpoint !== '2xl';
  const usesConcurrentChatLayout = isTextChatOpen && breakpoint === '2xl';
  const usesChapterPanel = breakpoint === '2xl';
  const usesInlineChapterSelector = breakpoint === 'base' || breakpoint === '3xs' || breakpoint === '2xs';
  const usesConcurrentChapterLayout = showChapterList && usesChapterPanel;
  const hidesConversationBarForChat = isTextChatOpen && breakpoint === '2xl';

  const sizeMap: Record<string, ButtonSize> = {
    base: "md",
    "3xs": "md",
    "2xs": "md",
    xs: "md",
    sm: "md",
    md: "md",
    lg: "md",
    xl: "md",
    "2xl": "md",
  };

  const controlSize = sizeMap[breakpoint];

  // Keep the part of the player visible above the mobile chat sheet pinned to
  // the visual viewport while the keyboard pans the layout viewport.
  useLayoutEffect(() => {
    if (!isTextChatOpen || !usesMobileChatLayout) return;

    const content = viewportContentRef.current;
    if (!content) return;

    let acceptedTop = 0;
    let acceptedScrollY = window.scrollY;

    const stopObserving = observeStableVisualViewport(({ top }) => {
      acceptedTop = top;
      acceptedScrollY = window.scrollY;
      content.style.top = `${top}px`;
    }, ({ top }) => {
      const viewportDelta = top - acceptedTop;
      const scrollDelta = window.scrollY - acceptedScrollY;
      const transientDelta = Math.abs(viewportDelta) >= Math.abs(scrollDelta)
        ? viewportDelta
        : scrollDelta;
      content.style.top = `${acceptedTop + transientDelta}px`;
    });

    return () => {
      stopObserving();
      content.style.removeProperty('top');
    };
  }, [isTextChatOpen, usesMobileChatLayout]);

  const chapterButtonSizes: Record<ButtonSize, string> = {
    base: 'w-12 h-12',
    sm: 'w-12 h-12',
    md: 'w-14 h-14',
    lg: 'w-16 h-16',
    xl: 'w-18 h-18',
  };

  const chapterIconSizes: Record<ButtonSize, 'sm' | 'md' | 'lg' | 'xl'> = {
    base: 'md',
    sm: 'md',
    md: 'lg',
    lg: 'lg',
    xl: 'xl',
  };

  const iconControlClassName = `flex justify-center items-center aspect-square rounded-full transition-colors cursor-pointer ${chapterButtonSizes[controlSize]} ${
    isDarkMode
      ? 'text-gray-200'
      : 'text-gray-800'
  }`;

  useClickOutside(chapterSelectorRef, () => {
    if (!usesChapterPanel) setShowChapterList(false);
  });
  useClickOutside(sleepTimerRef, () => {
    setShowSleepTimer(false);
  });

  useEffect(() => {
    if ((!showChapterList || !usesChapterPanel) && !showSleepTimer && !showMore) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowChapterList(false);
        setShowSleepTimer(false);
        setShowMore(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showChapterList, showSleepTimer, showMore, usesChapterPanel]);

  const trackPlayerEvent = (eventName: string, extraProps = {}) => {
    bookAnalytics.trackPlayerEvent(eventName, {
      book,
      progressSeconds: getProgressSeconds(),
      isPlaying,
      isConnected,
      surface: 'reader',
    }, extraProps);
  };

  const handleChapterSelect = (
    chapterId: string,
    surface: 'popup' | 'panel' | 'in_text_link',
    options?: { fragmentId?: string | null },
  ) => {
    const toIndex = chapters.findIndex((c) => c.id === chapterId);
    trackPlayerEvent('Chapter Selected', {
      from_chapter_id: currentChapter?.id,
      to_chapter_id: chapterId,
      chapter_index: toIndex,
      total_chapters: chapters.length,
      chapter_selector_surface: surface,
      trigger: 'button',
      input_method: 'unknown',
    });
    onChapterSelect(chapterId, options);
  };

  const handleTranscriptSeekAndPlay = (time: number, targetChapterId?: string) => {
    trackPlayerEvent('Transcript Tap To Seek', {
      source_chapter_id: currentChapter?.id,
      target_chapter_id: targetChapterId,
      target_ms: Math.round(time * 1000),
      cross_chapter: !!targetChapterId && targetChapterId !== currentChapter?.id,
      trigger: 'button',
      input_method: 'unknown',
    });
    onSeekAndPlay(time, targetChapterId);
  };

  const showVisualizer = isBuffering || isConnected;

  // The sheet grows out of the transport controls; when a voice session or
  // the chat takes their place, it has nothing to stand on.
  useEffect(() => {
    if (showVisualizer || isTextChatOpen) {
      setShowMore(false);
    }
  }, [showVisualizer, isTextChatOpen]);

  const toggleMore = () => {
    if (!usesChapterPanel) setShowChapterList(false);
    setShowPlaybackSpeed(false);
    setShowSleepTimer(false);
    if (!showMore) {
      setMoreSurfaceMounted(true);
      trackPlayerEvent('Player Menu Opened', { trigger: 'button', input_method: 'unknown' });
    }
    setShowMore(!showMore);
  };

  // One set of sleep-timer actions for both of its surfaces: the popover on
  // the web and the view inside the "more" sheet in the native app.
  const selectSleepMinutes = (minutes: number) => {
    trackPlayerEvent('Sleep Timer Set', {
      mode: 'countdown',
      minutes,
      trigger: 'button',
      input_method: 'unknown',
    });
    onSetCountdownSleepTimer(minutes);
  };
  const selectSleepEndOfChapter = () => {
    trackPlayerEvent('Sleep Timer Set', {
      mode: 'end_of_chapter',
      trigger: 'button',
      input_method: 'unknown',
    });
    onSetEndOfChapterSleepTimer();
  };
  const cancelSleepTimer = () => {
    trackPlayerEvent('Sleep Timer Cancelled', {
      mode: sleepTimer?.mode,
      remaining_seconds: sleepTimerRemainingSeconds,
      trigger: 'button',
      input_method: 'unknown',
    });
    onCancelSleepTimer();
  };

  const chapterSelectorButtonProps = {
    type: 'button',
    'aria-label': 'Select chapter',
    'aria-expanded': showChapterList,
    'aria-controls': usesChapterPanel ? 'reader-chapter-panel' : 'chapter-popup-list',
    onClick: () => {
      setShowSleepTimer(false);
      setShowPlaybackSpeed(false);
      setShowMore(false);
      setShowChapterList((open) => !open);
    },
  } as const;

  const chapterPopup = (
    <ControlPopupPresence isOpen={showChapterList && !usesChapterPanel}>
      {(animationState) => (
          <ChapterList
            chapters={chapters}
            currentChapterId={currentChapter?.id}
            animationClassName={`control-popup-native-${animationState}`}
            popupAlign={usesInlineChapterSelector ? 'center' : 'start'}
            onChapterSelect={(chapterId) => {
              handleChapterSelect(chapterId, 'popup');
              setShowChapterList(false);
            }}
          />
      )}
    </ControlPopupPresence>
  );

  const sleepTimerPopup = (
    <ControlPopupPresence isOpen={showSleepTimer}>
      {(animationState) => (
        <SleepTimerMenu
          id="reader-sleep-timer-menu"
          sleepTimer={sleepTimer}
          remainingSeconds={sleepTimerRemainingSeconds}
          animationClassName={`control-popup-native-${animationState}`}
          placement="above"
          onSelectMinutes={selectSleepMinutes}
          onSelectEndOfChapter={selectSleepEndOfChapter}
          onCancel={cancelSleepTimer}
          onClose={() => setShowSleepTimer(false)}
        />
      )}
    </ControlPopupPresence>
  );

  const sleepTimerDot = sleepTimer && (
    <span
      className={`absolute top-2 right-2 h-2 w-2 rounded-full ${isDarkMode ? 'bg-white' : 'bg-black'}`}
      aria-hidden="true"
    />
  );

  // The leading transport button: "more" in the native app, the sleep timer
  // itself on the web. Either way an active timer shows as a dot on it.
  const leadingMenuControl = usesMoreSheet ? (
    <div className="relative shrink-0">
      <button
        type="button"
        aria-label={sleepTimer
          ? `More options. ${getSleepTimerButtonLabel(sleepTimer, sleepTimerRemainingSeconds)}`
          : 'More options'}
        aria-haspopup="dialog"
        aria-expanded={showMore}
        aria-controls="reader-more-sheet"
        onClick={toggleMore}
        className={`relative flex justify-center items-center aspect-square rounded-full transition-colors cursor-pointer ${chapterButtonSizes[controlSize]} ${
          showMore
            ? isDarkMode ? 'bg-zinc-700 text-gray-100' : 'bg-blue-100 text-blue-700'
            : isDarkMode ? 'text-gray-200' : 'text-gray-800'
        }`}
      >
        <MoreOptionsIcon size={chapterIconSizes[controlSize] === 'md' ? 'md' : 'lg'} />
        {sleepTimerDot}
      </button>
    </div>
  ) : (
    <div ref={sleepTimerRef} className="relative shrink-0">
      <button
        type="button"
        aria-label={getSleepTimerButtonLabel(sleepTimer, sleepTimerRemainingSeconds)}
        aria-expanded={showSleepTimer}
        aria-controls="reader-sleep-timer-menu"
        aria-pressed={Boolean(sleepTimer)}
        onClick={() => {
          if (!usesChapterPanel) setShowChapterList(false);
          setShowPlaybackSpeed(false);
          setShowSleepTimer((open) => !open);
        }}
        className={`${iconControlClassName} relative`}
      >
        <ClockIcon size={chapterIconSizes[controlSize]} />
        {sleepTimerDot}
      </button>

      {sleepTimerPopup}
    </div>
  );

  const bindChatSwipe = useDrag(({ swipe: [, swipeY] }) => {
    if (swipeY === -1) {
      onOpenTextChat?.(undefined, true, {
        surface: 'reader',
        trigger: 'swipe',
        input_method: 'touch',
      });
    }
  }, {
    enabled: isMobileOrTablet && !isTextChatOpen,
    pointer: { touch: true, capture: false },
    swipe: { distance: 20, velocity: 0.2 },
  });

  return (
    <main
      id="main-content"
      className={`relative ${themeClasses} mt-[calc(4.25rem_+_var(--safe-top))] h-[calc(100dvh-4.25rem-var(--safe-top))] overflow-hidden`}
      style={{ backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)' }}
    >
      <motion.div
        ref={viewportContentRef}
        className="absolute inset-0 flex flex-col px-safe"
        initial={false}
        animate={{
          left: usesConcurrentChapterLayout ? READER_SIDE_PANEL_WIDTH_PX : 0,
          right: usesConcurrentChatLayout ? READER_SIDE_PANEL_WIDTH_PX : 0,
        }}
        transition={READER_SIDE_PANEL_SPRING_TRANSITION}
      >
      {/* Transcription */}
      <div data-tutorial-id="player-transcript" className="relative grow flex items-center justify-center min-h-0 overflow-visible">
        <div className="absolute inset-0 overflow-visible">
          {isLoadingReaderBook ? (
            <div className="h-full flex items-center justify-center" role="status" aria-live="polite">
              Loading reader…
            </div>
          ) : readerBookError ? (
            <div className="h-full flex flex-col gap-3 items-center justify-center p-6" role="alert">
              <p>Reader unavailable: {readerBookError}</p>
              <button type="button" className="rounded-full border px-4 py-2" onClick={onRetryReaderBook}>
                Retry reader
              </button>
            </div>
          ) : shouldMountTranscript ? (
            <TranscribePlayback
              audioRef={audioRef}
              currentChapter={currentChapter}
              readerBook={readerBook}
              isRenderProfileSupported={isRenderProfileSupported}
              runtime={chapterRuntime}
              chapterTimingsStatus={chapterTimingsStatus}
              selectedReadingVoiceKey={selectedReadingVoiceKey}
              isVoiceSessionActive={isConnected || isBuffering}
              onSeekAndPlay={handleTranscriptSeekAndPlay}
              onNavigateChapter={(chapterId, options) =>
                handleChapterSelect(chapterId, 'in_text_link', options)}
              isOpen
              chapterSelectionRequest={chapterSelectionRequest}
              chapterRecoveryNotice={chapterRecoveryNotice}
              positionRequest={positionRequest}
              pendingResumeSeconds={pendingResumeSeconds}
              onReaderContentReadinessChange={onReaderContentReadinessChange}
              onReaderPositioned={onReaderPositioned}
              conversationSessions={conversationSessions}
              activeConversationId={activeConversationId}
              onOpenConversation={onOpenConversation}
            />
          ) : null}
        </div>

      </div>

      {usesMoreSheet && <PlayerMoreScrim isOpen={showMore} onClose={() => setShowMore(false)} />}

      {/* Bottom Controls — keep the play controls above the home indicator.
          max(), not +: the base padding already covers most of the inset, so
          stacking the two left a dead band under the controls on iPhone. */}
      <div
        className={`relative shrink-0 w-full px-8 pb-[max(1.875rem,var(--safe-bottom))] landscape-compact:pb-[max(1rem,var(--safe-bottom))]2xl:grid 2xl:grid-cols-[minmax(0,1fr)_minmax(0,38rem)_minmax(0,1fr)] 2xl:items-end 2xl:gap-x-16 2xl:px-5 ${
          showSleepTimer || showPlaybackSpeed || moreSurfaceMounted || (showChapterList && !usesChapterPanel) ? 'z-50' : 'z-30'
        } ${
          showVisualizer ? '2xl:gap-y-4' : '2xl:gap-y-6.5'
        }`}
        // Opaque only while the sheet is about: the controls and the sheet
        // above them are then one panel standing in front of the scrim.
        style={moreSurfaceMounted
          ? { backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)' }
          : undefined}
      >
        {usesMoreSheet && (
          <PlayerMoreSheet
            id="reader-more-sheet"
            isOpen={showMore}
            onClose={() => setShowMore(false)}
            onExitComplete={() => setMoreSurfaceMounted(false)}
            book={book}
            readerBook={readerBook}
            voiceKey={selectedReadingVoiceKey}
            sleepTimer={sleepTimer}
            sleepTimerRemainingSeconds={sleepTimerRemainingSeconds}
            onSelectSleepMinutes={selectSleepMinutes}
            onSelectSleepEndOfChapter={selectSleepEndOfChapter}
            onCancelSleepTimer={cancelSleepTimer}
          />
        )}
        {showVisualizer ? (
          <div className="w-full max-w-lg mx-auto mb-4 mt-1 flex justify-center items-center h-48 landscape-compact:h-35 2xl:h-35 2xl:col-start-2 2xl:row-start-2 2xl:mb-0 2xl:max-w-[313px]">
            {rtviClientRef.current && (
              <PipecatClientProvider client={rtviClientRef.current as never}>
                <ConversationVisualizer
                  participantType={currentSpeaker}
                  barColor={currentSpeaker === 'bot' ? 'purple' : 'blue'}
                  barCount={48}
                  barGap={2}
                  barWidth={5.25}
                  barMaxHeight={96}
                  edgeRamp={0.75}
                />
              </PipecatClientProvider>
            )}
          </div>
        ) : (
          <>
          {usesInlineChapterSelector && (
            <div className="mb-2 mt-2 flex w-full justify-center">
              <div ref={chapterSelectorRef} className="relative min-w-0 max-w-full">
                <button
                  {...chapterSelectorButtonProps}
                  className={`flex max-w-full cursor-pointer items-center gap-2 rounded-full px-2 py-1 text-sm ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-800'
                  }`}
                >
                  <span className="shrink-0">
                    <ChapterSelectionIcon size="sm" />
                  </span>
                  <span className="truncate">{currentChapter?.title ?? 'Chapters'}</span>
                </button>

                {chapterPopup}
              </div>
            </div>
          )}
          <div className="mx-auto mb-6 h-25 landscape-compact:mb-2 landscape-compact:h-20 flex w-full max-w-152 flex-col justify-between 2xl:contents">
            <div className={`relative flex h-5 w-full items-center gap-3 tabular-nums 2xl:col-span-3 2xl:col-start-1 2xl:row-start-1 ${isDarkMode ? '2xl:text-theme-primary-dark' : '2xl:text-blue-500'}`}>
              {/* Progress bar: perfectly centered vertically */}
              <div className="z-10 flex min-w-0 flex-1 items-center">
                {/* py-2 */}
                <ProgressBar
                  duration={duration}
                  currentTime={currentTime}
                  onSeek={onSeek}
                  isPlaying={isPlaying}
                  audioRef={audioRef}
                  className="w-full rounded-full"
                  containerClassName="2xl:h-2"
                  useLargeHitArea={true}
                  imperativePlayback={true}
                  pendingResumeSeconds={pendingResumeSeconds}
                />
              </div>

              <ReaderTimeInfo
                audioRef={audioRef}
                currentTime={currentTime}
                duration={duration}
                imperativePlayback={true}
                pendingResumeSeconds={pendingResumeSeconds}
              />
            </div>

            <div className="mx-auto w-full max-w-lg 2xl:col-start-2 2xl:row-start-2">
              <PlayerControls
                isPlaying={isPlaying}
                isReady={isReady}
                isBuffering={isBuffering}
                isConnected={isConnected}
                onTogglePlay={onTogglePlay}
                onSkipBackward={onSkipBackward}
                onSkipForward={onSkipForward}
                size={controlSize}
                distributed={breakpoint !== '2xl'}
                plainPlayButton
                leadingControl={
                  <>
                  {!usesInlineChapterSelector && (
                    <div ref={chapterSelectorRef} className="relative shrink-0">
                      <button
                        {...chapterSelectorButtonProps}
                        className={iconControlClassName}
                      >
                        <ChapterSelectionIcon size={chapterIconSizes[controlSize]} />
                      </button>

                      {chapterPopup}
                    </div>
                  )}

                  {leadingMenuControl}
                  </>
                }
                trailingControl={
                  <>
                  <PlaybackSpeedControl
                    id="reader-playback-speed-menu"
                    size={controlSize}
                    playbackRate={playbackRate}
                    onPlaybackRateChange={onPlaybackRateChange}
                    isOpen={showPlaybackSpeed}
                    onToggle={() => {
                      if (!usesChapterPanel) setShowChapterList(false);
                      setShowSleepTimer(false);
                      setShowMore(false);
                      setShowPlaybackSpeed((open) => !open);
                    }}
                    onClose={() => setShowPlaybackSpeed(false)}
                  />

                  {!usesInlineChapterSelector && (
                    <div className={`shrink-0 ${chapterButtonSizes[controlSize]}`} aria-hidden="true" />
                  )}
                  </>
                }
              />
            </div>
          </div>
          </>
        )}

        {!showVisualizer && (
          <div className="hidden 2xl:col-start-1 2xl:row-start-2 2xl:flex 2xl:h-14 2xl:w-full 2xl:min-w-0 2xl:items-center 2xl:gap-5 2xl:overflow-visible 2xl:justify-self-start">
            <div className="h-18 w-18 shrink-0 overflow-hidden rounded-sm">
              <img
                src={book.coverUrl}
                alt={`${book.title} cover`}
                className="h-full w-full object-cover"
                style={{ objectPosition: 'bottom' }}
              />
            </div>
            <div className="flex min-w-0 flex-col text-left">
              <h3 className={`portrait-text-app line-clamp-2 font-regular text-xl ${isDarkMode ? 'text-theme-primary-dark' : 'text-gray-800'}`}>
                <span>{book.title}</span>
                {book.subheading && (
                  <span>:&nbsp;{book.subheading}</span>
                )}
              </h3>
              <p className={`line-clamp-1 text-sm ${isDarkMode ? 'text-theme-secondary-dark' : 'text-black-700'}`}>
                by {book.author}
              </p>
            </div>
          </div>
        )}

        <motion.div
          className={`mx-auto w-full ${
          showVisualizer
            ? 'max-w-91 2xl:col-start-2 2xl:row-start-3 2xl:justify-self-center'
            : 'max-w-152 2xl:col-start-3 2xl:row-start-2 2xl:mx-0 2xl:max-w-100 2xl:justify-self-end'
          }`}
          initial={false}
          animate={{
            opacity: hidesConversationBarForChat ? 0 : 1,
            x: hidesConversationBarForChat ? 24 : 0,
            scale: hidesConversationBarForChat ? 0.98 : 1,
          }}
          transition={hidesConversationBarForChat
            ? { duration: 0.12, ease: 'easeOut' }
            : { duration: 0.18, delay: 0.12, ease: 'easeOut' }}
          aria-hidden={hidesConversationBarForChat}
          inert={hidesConversationBarForChat}
          style={{ pointerEvents: hidesConversationBarForChat ? 'none' : 'auto' }}
        >
          <div
            className="flex flex-col items-center w-full"
            style={{ touchAction: isMobileOrTablet ? 'none' : 'auto' }}
            {...bindChatSwipe()}
          >
            <div className="flex w-full justify-center">
              <AlephEntry
                book={book}
                getProgressSeconds={getProgressSeconds}
                analyticsSurface="reader"
                onConnect={onConnect}
                onDisconnect={onDisconnect}
                isConnected={isConnected}
                connectionStatus={connectionStatus}
                isBuffering={isBuffering}
                onType={(context) => onOpenTextChat?.(undefined, true, context)}
                hideMobileTextComposer={isTextChatOpen && usesMobileChatLayout}
                draftText={textChatDraft}
                size={controlSize}
                compact
                variant="expanded"
                matchConversationSurface
                mode={conversationMode}
                onModeChange={onConversationModeChange}
              />
            </div>
          </div>
        </motion.div>
      </div>
      </motion.div>
      {usesChapterPanel && (
        <ChapterList
          chapters={chapters}
          currentChapterId={currentChapter?.id}
          onChapterSelect={(chapterId) => handleChapterSelect(chapterId, 'panel')}
          variant="panel"
          isOpen={showChapterList}
          onClose={() => setShowChapterList(false)}
        />
      )}
    </main>
  );
};

const areReadPlayerPropsEqual = (previous: ReadPlayerProps, next: ReadPlayerProps) => {
  const nonTimePropsMatch =
    previous.book === next.book &&
    previous.isPlaying === next.isPlaying &&
    previous.isReady === next.isReady &&
    previous.duration === next.duration &&
    previous.isConnected === next.isConnected &&
    previous.connectionStatus === next.connectionStatus &&
    previous.isBuffering === next.isBuffering &&
    previous.currentChapter === next.currentChapter &&
    previous.chapters === next.chapters &&
    previous.audioRef === next.audioRef &&
    previous.rtviClientRef === next.rtviClientRef &&
    previous.currentSpeaker === next.currentSpeaker &&
    previous.getProgressSeconds === next.getProgressSeconds &&
    previous.readerBook === next.readerBook &&
    previous.isRenderProfileSupported === next.isRenderProfileSupported &&
    previous.chapterRuntime === next.chapterRuntime &&
    previous.chapterTimingsStatus === next.chapterTimingsStatus &&
    previous.selectedReadingVoiceKey === next.selectedReadingVoiceKey &&
    previous.isLoadingReaderBook === next.isLoadingReaderBook &&
    previous.readerBookError === next.readerBookError &&
    previous.chapterRecoveryNotice === next.chapterRecoveryNotice &&
    previous.chapterSelectionRequest === next.chapterSelectionRequest &&
    previous.positionRequest === next.positionRequest &&
    previous.pendingResumeSeconds === next.pendingResumeSeconds &&
    previous.isTextChatOpen === next.isTextChatOpen &&
    previous.textChatDraft === next.textChatDraft &&
    previous.conversationSessions === next.conversationSessions &&
    previous.activeConversationId === next.activeConversationId &&
    previous.onOpenConversation === next.onOpenConversation &&
    previous.sleepTimer === next.sleepTimer &&
    previous.sleepTimerRemainingSeconds === next.sleepTimerRemainingSeconds &&
    previous.onSetCountdownSleepTimer === next.onSetCountdownSleepTimer &&
    previous.onSetEndOfChapterSleepTimer === next.onSetEndOfChapterSleepTimer &&
    previous.onCancelSleepTimer === next.onCancelSleepTimer &&
    previous.playbackRate === next.playbackRate &&
    previous.onPlaybackRateChange === next.onPlaybackRateChange &&
    previous.conversationMode === next.conversationMode &&
    previous.onConversationModeChange === next.onConversationModeChange;

  if (!nonTimePropsMatch) {
    return false;
  }

  return true;
};

const ReadPlayer = React.memo(ReadPlayerComponent, areReadPlayerPropsEqual);

export default ReadPlayer;
